/**
 * The menu behind a right click, wherever it was clicked.
 *
 * One menu for everything: the world, the bag, a gauge. What changes is the list
 * of entries; how it looks, where it opens and how it closes do not, which is
 * why somebody who has used one can use all of them.
 *
 * It is measured once it is drawn and then placed. Opening down and to the right
 * of the pointer is the habit, and near an edge it opens the other way instead
 * of hanging off the screen: a menu that can only be half read is a menu with
 * half its choices missing.
 */

import { useLayoutEffect, useRef, useState } from 'react';
import type { ContextMenu } from '../game/menu';
import { placeMenu } from './menu-place';

export function ContextMenuView({
  menu,
  onClose,
  onReopen,
}: {
  menu: ContextMenu;
  onClose: () => void;
  /** A second right click somewhere else, which opens the menu for there. */
  onReopen?: (x: number, y: number) => void;
}): JSX.Element {
  const box = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<{ left: number; top: number } | null>(null);

  // Before it is painted, so it never flashes up in the wrong place first.
  useLayoutEffect(() => {
    const held = box.current;
    if (!held) return;

    const { width, height } = held.getBoundingClientRect();
    setPlace(
      placeMenu(
        { x: menu.screenX, y: menu.screenY },
        { width, height },
        { width: window.innerWidth, height: window.innerHeight },
      ),
    );
  }, [menu]);

  return (
    <>
      {/*
        Anywhere outside the menu closes it. A right click closes it and opens
        the menu for what is under the new point, rather than being swallowed,
        which is what a player expects from a second right click.
      */}
      <div
        className="menu-shade"
        onPointerDown={onClose}
        onContextMenu={(event) => {
          event.preventDefault();
          if (onReopen) onReopen(event.clientX, event.clientY);
          else onClose();
        }}
      />
      <div
        className="menu"
        ref={box}
        style={
          place
            ? { left: place.left, top: place.top }
            : { left: menu.screenX, top: menu.screenY, visibility: 'hidden' }
        }
        onContextMenu={(event) => event.preventDefault()}
      >
        <div className="menu-title">{menu.title}</div>
        {menu.entries.map((entry, index) =>
          entry.heading ? (
            <div className="menu-heading" key={`${entry.label}-${index}`}>
              {entry.label}
            </div>
          ) : (
            <button
              key={`${entry.label}-${index}`}
              type="button"
              className={[
                'menu-entry',
                entry.pending ? 'pending' : '',
                entry.checked ? 'checked' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => {
                entry.run();
                onClose();
              }}
            >
              <span>{entry.label}</span>
              {entry.detail ? <span className="menu-detail">{entry.detail}</span> : null}
            </button>
          ),
        )}
      </div>
    </>
  );
}
